import { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../api.js';
import { PALETTE } from '../palette.js';
import FunnelReadinessPanel from '../components/FunnelReadinessPanel.jsx';
import { Button, Pill } from '../components/primitives.jsx';
import ProtectedPageLoader from '../components/ProtectedPageLoader.jsx';
import ProtectedVideo from '../components/ProtectedVideo.jsx';
import {
  IconCheck,
  IconChevron,
  IconExternal,
  IconInfo,
  IconPlay,
  IconSpark,
  IconTarget,
  IconVideo,
} from '../components/icons.jsx';
import { safeFailureMessage } from '../safe-failure.js';
import { refreshSaasBrandResearch } from '../saas-brand-research.js';

function tenantHeaders(companyId, headers = {}) {
  return companyId ? { headers: { 'x-company-id': companyId, ...headers } } : undefined;
}

function activeCompanyId(session) {
  const memberships = session.user?.memberships || [];
  return session.user?.activeCompany?.id || memberships[0]?.companyId || '';
}

function activeRole(session) {
  const companyId = activeCompanyId(session);
  return session.user?.memberships?.find((membership) => membership.companyId === companyId)?.role
    || session.user?.activeCompany?.role
    || '';
}

const REVISION_AREAS = Object.freeze([
  { id: 'brief', label: 'Brief' },
  { id: 'funnel', label: 'Funnel' },
  { id: 'scripts', label: 'Ad scripts' },
  { id: 'videos', label: 'Videos' },
  { id: 'publish', label: 'Publish setup' },
]);

function fmtDateTime(value) {
  if (!value) return '-';
  return new Date(value).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function statusTone(status) {
  if (['approved', 'succeeded', 'published', 'available', 'ready'].includes(status)) return 'live';
  if (['pending', 'needs_review', 'revising', 'waiting_approval', 'not_requested', 'unpublished', 'rollback'].includes(status)) return 'accent';
  if (['failed', 'rejected', 'canceled', 'unavailable', 'disabled', 'not_approved', 'needs_attention', 'partial'].includes(status)) return 'warn';
  return 'neutral';
}

// A rejected campaign returns to needs_review, whose label ("Needs Review")
// reads as "nobody has decided yet". Name the decision instead so the hero
// never contradicts the Approval chip.
export function campaignReviewHeroStatus(review) {
  const status = review?.campaign?.status;
  if (status === 'needs_review' && review?.approval?.status === 'rejected') {
    return { status: 'rejected', label: 'Changes Requested' };
  }
  return {
    status,
    label: review?.campaign?.lifecycle?.label || labelize(status),
  };
}

// Header chips carry one fact each. The campaign lifecycle already owns the
// review/publish narrative through the hero pill, and a finished generation
// pipeline has nothing left to say, so neither is repeated here.
export function campaignReviewSummary(review) {
  if (!review) return [];
  const workflowStatus = review.workflowJob?.progress?.status || review.workflowJob?.status;
  const workflowRunning = !['succeeded', 'waiting_approval'].includes(String(workflowStatus || ''));
  return [
    ...(workflowStatus && workflowRunning
      ? [{
        label: 'Workflow',
        value: review.workflowJob?.progress?.statusLabel || labelize(workflowStatus),
      }]
      : []),
    { label: 'Funnel', value: labelize(review.funnel?.publication?.status || review.funnel?.status, 'Unavailable') },
    { label: 'Approval', value: labelize(review.approval?.status, 'Pending') },
    { label: 'Meta publish', value: labelize(review.publish?.status, 'Not requested') },
  ];
}

function labelize(value, fallback = 'Not set') {
  const text = String(value || '').trim();
  if (!text) return fallback;
  return text
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function compactJsonEntries(value = {}) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return [];
  return Object.entries(value)
    .filter(([, item]) => item !== null && item !== undefined && item !== '')
    .slice(0, 8);
}

function valuePreview(value) {
  if (Array.isArray(value)) return `${value.length} item${value.length === 1 ? '' : 's'}`;
  if (value && typeof value === 'object') return compactJsonEntries(value).map(([key]) => labelize(key)).join(', ') || 'Details available';
  return String(value || '-');
}

export function artifactRouteForReview(review, artifactTypes = []) {
  const artifact = (review?.artifacts || []).find((entry) => artifactTypes.includes(entry?.type));
  return artifact?.action?.type === 'internal_route' && artifact.action.route
    ? artifact.action.route
    : null;
}

function normalizedVideoIdentity(value) {
  const text = String(value || '').trim().toLowerCase();
  if (!text) return '';
  const basename = text.split(/[\\/]/).pop().replace(/\.[a-z0-9]{2,5}$/i, '');
  return basename.replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}

function videoIdentitySet(values) {
  return new Set(values.map(normalizedVideoIdentity).filter(Boolean));
}

function setsIntersect(left, right) {
  for (const value of left) {
    if (right.has(value)) return true;
  }
  return false;
}

function videoMetadataIdentity(item) {
  const value = item && typeof item === 'object' ? item : { filename: item };
  return {
    strong: videoIdentitySet([value.artifactId, value.artifact_id, value.id, value.scriptId, value.script_id]),
    filename: videoIdentitySet([value.filename]),
    title: videoIdentitySet([value.title, value.scriptTitle, value.script_title]),
  };
}

function videoArtifactIdentity(artifact) {
  return {
    strong: videoIdentitySet([
      artifact.id,
      artifact.metadata?.artifactId,
      artifact.metadata?.artifact_id,
      artifact.metadata?.scriptId,
      artifact.metadata?.script_id,
    ]),
    filename: videoIdentitySet([artifact.metadata?.filename, artifact.label]),
    title: videoIdentitySet([artifact.metadata?.title, artifact.metadata?.scriptTitle]),
  };
}

function singleIdentityMatch(candidates, itemIdentity, identityField) {
  const matches = candidates.filter((artifact) => (
    setsIntersect(itemIdentity[identityField], videoArtifactIdentity(artifact)[identityField])
  ));
  return matches.length === 1 ? matches[0] : null;
}

function matchingVideoArtifact(item, metadataItems, artifacts, claimedArtifactIds) {
  const identity = videoMetadataIdentity(item);
  const candidates = artifacts.filter((artifact) => !claimedArtifactIds.has(artifact.id));
  const strongMatch = singleIdentityMatch(candidates, identity, 'strong');
  if (strongMatch) return strongMatch;
  const filenameMatch = singleIdentityMatch(candidates, identity, 'filename');
  if (filenameMatch) return filenameMatch;
  if (!identity.title.size) return null;
  const metadataTitleMatches = metadataItems.filter((entry) => (
    setsIntersect(identity.title, videoMetadataIdentity(entry).title)
  ));
  if (metadataTitleMatches.length !== 1) return null;
  return singleIdentityMatch(candidates, identity, 'title');
}

export function isVideoArtifact(artifact) {
  return artifact?.type === 'video' || Boolean(artifact?.contentType?.startsWith('video/'));
}

export function buildCampaignReviewVideos(review) {
  const metadataItems = Array.isArray(review?.sections?.videos) ? review.sections.videos : [];
  const videoArtifacts = (review?.artifacts || []).filter(isVideoArtifact);
  const claimedArtifactIds = new Set();
  const unmatchedMetadataIdentities = [];
  const items = metadataItems.map((entry, index) => {
    const metadata = entry && typeof entry === 'object' ? entry : { filename: entry };
    const artifact = matchingVideoArtifact(metadata, metadataItems, videoArtifacts, claimedArtifactIds);
    if (artifact?.id) claimedArtifactIds.add(artifact.id);
    if (!artifact) unmatchedMetadataIdentities.push(videoMetadataIdentity(metadata));
    const signedUrl = artifact?.signedUrl?.url || null;
    const title = metadata.title || metadata.scriptTitle || metadata.filename || artifact?.label || `Video ${index + 1}`;
    return {
      key: artifact?.id || metadata.artifactId || metadata.id || metadata.scriptId || metadata.filename || `video-${index + 1}`,
      artifactId: artifact?.id || metadata.artifactId || null,
      scriptId: metadata.scriptId || metadata.script_id || artifact?.metadata?.scriptId || null,
      filename: metadata.filename || artifact?.metadata?.filename || artifact?.label || '',
      title,
      durationSeconds: metadata.durationSeconds || metadata.duration_seconds || null,
      concept: metadata.concept || metadata.angle || '',
      signedUrl,
      contentType: artifact?.contentType || 'video/mp4',
      availability: signedUrl ? {
        status: 'available',
        message: 'Playable preview available.',
      } : {
        status: artifact?.availability?.status || 'unavailable',
        message: artifact?.availability?.message
          || (artifact
            ? 'The stored video exists, but a playable preview is unavailable.'
            : 'Generated video metadata exists, but the stored video artifact is unavailable.'),
      },
    };
  });

  videoArtifacts.forEach((artifact) => {
    if (claimedArtifactIds.has(artifact.id)) return;
    const artifactIdentity = videoArtifactIdentity(artifact);
    const conflictsWithUnmatchedMetadata = unmatchedMetadataIdentities.some((identity) => (
      setsIntersect(identity.filename, artifactIdentity.filename)
      || setsIntersect(identity.title, artifactIdentity.title)
    ));
    if (conflictsWithUnmatchedMetadata) return;
    const signedUrl = artifact.signedUrl?.url || null;
    items.push({
      key: artifact.id,
      artifactId: artifact.id,
      scriptId: artifact.metadata?.scriptId || null,
      filename: artifact.metadata?.filename || artifact.label || '',
      title: artifact.metadata?.title || artifact.label || `Video ${items.length + 1}`,
      durationSeconds: artifact.metadata?.durationSeconds || null,
      concept: artifact.metadata?.concept || '',
      signedUrl,
      contentType: artifact.contentType || 'video/mp4',
      availability: signedUrl ? {
        status: 'available',
        message: 'Playable preview available.',
      } : {
        status: artifact.availability?.status || 'unavailable',
        message: artifact.availability?.message || 'The stored video exists, but a playable preview is unavailable.',
      },
    });
  });

  const playableCount = items.filter((item) => Boolean(item.signedUrl)).length;
  return {
    items,
    generatedCount: items.length,
    playableCount,
    unavailableCount: items.length - playableCount,
  };
}

export default function CampaignReview({ navigate, params, session }) {
  const workflowJobId = params[0] || '';
  const companyId = activeCompanyId(session);
  const canManageFunnel = ['owner', 'admin'].includes(activeRole(session));
  const isSaasSession = session.user?.auth?.type === 'supabase';
  const activeReviewContextKey = `${companyId}:${workflowJobId}`;
  const [status, setStatus] = useState('idle');
  const [storedReview, setStoredReview] = useState(null);
  const [storedReviewContextKey, setStoredReviewContextKey] = useState('');
  const [error, setError] = useState(null);
  const [actionState, setActionState] = useState({ status: 'idle', message: '' });
  const [funnelActionState, setFunnelActionState] = useState({ status: 'idle', message: '' });
  const [funnelCompletionState, setFunnelCompletionState] = useState({ status: 'idle', message: '' });
  const [funnelWorkbench, setFunnelWorkbench] = useState(null);
  const [activeSection, setActiveSection] = useState('brief');
  const [revisionForm, setRevisionForm] = useState({ open: false, feedback: '', affectedAreas: [], status: 'idle', error: '' });
  const requestVersion = useRef(0);
  const publicationRequest = useRef({ context: '', idempotencyKey: '' });
  const revisionRequest = useRef({ context: '', idempotencyKey: '' });
  const activeReviewContext = useRef(activeReviewContextKey);
  activeReviewContext.current = activeReviewContextKey;
  const reviewContextCurrent = storedReviewContextKey === activeReviewContextKey;
  const review = reviewContextCurrent ? storedReview : null;

  async function loadReview(nextStatus = 'loading') {
    if (!isSaasSession || !companyId || !workflowJobId) return false;
    const version = ++requestVersion.current;
    setStatus(nextStatus);
    setError(null);
    try {
      const response = await api.get(`/api/saas/workflows/${workflowJobId}/review`, tenantHeaders(companyId));
      if (version !== requestVersion.current) return false;
      setStoredReview(response.review || null);
      setStoredReviewContextKey(activeReviewContextKey);
      setStatus('ready');
      return true;
    } catch (err) {
      if (version !== requestVersion.current) return false;
      if (nextStatus === 'refreshing' && reviewContextCurrent && review) {
        setError(err);
        setStatus('ready');
        return false;
      }
      setStoredReview(null);
      setStoredReviewContextKey('');
      setError(err);
      setStatus('error');
      return false;
    }
  }

  useEffect(() => {
    requestVersion.current += 1;
    setStoredReview(null);
    setStoredReviewContextKey('');
    setError(null);
    setStatus('idle');
    setActionState({ status: 'idle', message: '' });
    setFunnelActionState({ status: 'idle', message: '' });
    setFunnelCompletionState({ status: 'idle', message: '' });
    setFunnelWorkbench(null);
    setActiveSection('brief');
    setRevisionForm({ open: false, feedback: '', affectedAreas: [], status: 'idle', error: '' });
    publicationRequest.current = { context: '', idempotencyKey: '' };
    revisionRequest.current = { context: '', idempotencyKey: '' };
    loadReview('loading');
    return () => { requestVersion.current += 1; };
  }, [isSaasSession, companyId, workflowJobId]);

  useEffect(() => {
    let active = true;
    const funnel = review?.funnel;
    const selectedVersionId = funnel?.generatedVersion?.id;
    if (!companyId || !funnel?.id || !selectedVersionId) {
      setFunnelWorkbench(null);
      return () => { active = false; };
    }
    setFunnelCompletionState({ status: 'loading', message: 'Loading funnel completion details...' });
    api.get(
      `/api/saas/funnels/${encodeURIComponent(funnel.id)}?versionId=${encodeURIComponent(selectedVersionId)}`,
      tenantHeaders(companyId),
    ).then((response) => {
      if (!active) return;
      setFunnelWorkbench(response);
      setFunnelCompletionState({ status: 'idle', message: '' });
    }).catch((requestError) => {
      if (!active) return;
      setFunnelWorkbench(null);
      setFunnelCompletionState({
        status: 'error',
        message: safeFailureMessage(requestError, 'funnel'),
      });
    });
    return () => { active = false; };
  }, [
    companyId,
    review?.funnel?.id,
    review?.funnel?.generatedVersion?.id,
  ]);

  async function runAction(action, handler) {
    if (actionState.status === 'working') return;
    const actionContext = activeReviewContextKey;
    setActionState({ status: 'working', message: action });
    try {
      await handler();
      if (activeReviewContext.current !== actionContext) return;
      const refreshed = await loadReview('refreshing');
      if (!refreshed) {
        setActionState({
          status: 'error',
          message: `${action} was accepted, but the latest status could not be loaded. Refresh to confirm.`,
        });
        return;
      }
      setActionState({ status: 'done', message: `${action} complete.` });
    } catch (err) {
      if (activeReviewContext.current !== actionContext) return;
      setActionState({ status: 'error', message: safeFailureMessage(err, 'approval') });
    }
  }

  async function openFunnelPreview() {
    const funnel = review?.funnel;
    if (!funnel?.id || !funnel.generatedVersion?.id || funnelActionState.status === 'working') return;
    const actionContext = activeReviewContextKey;
    const previewWindow = window.open('about:blank', '_blank');
    if (previewWindow) previewWindow.opener = null;
    setFunnelActionState({ status: 'working', message: 'Authorizing exact-version preview...' });
    try {
      const response = await api.post(
        `/api/saas/funnels/${funnel.id}/previews`,
        {
          versionId: funnel.generatedVersion.id,
          environment: funnel.environment,
          expiresInSeconds: funnel.preview?.expiresInSeconds || 900,
        },
        tenantHeaders(companyId),
      );
      if (!response.preview?.url) throw new Error('Funnel preview is unavailable');
      if (activeReviewContext.current !== actionContext) {
        previewWindow?.close();
        return;
      }
      setFunnelActionState({ status: 'done', message: 'Preview authorized.' });
      if (previewWindow) previewWindow.location.replace(response.preview.url);
      else window.location.assign(response.preview.url);
    } catch (err) {
      previewWindow?.close();
      if (activeReviewContext.current !== actionContext) return;
      setFunnelActionState({ status: 'error', message: safeFailureMessage(err, 'funnel') });
    }
  }

  async function publishFunnel() {
    const funnel = review?.funnel;
    const versionId = funnel?.generatedVersion?.id;
    if (!review?.actions?.canPublishFunnel || !funnel?.id || !versionId || funnelActionState.status === 'working') return;
    const actionContext = activeReviewContextKey;
    const context = `${companyId}:${workflowJobId}:${funnel.id}:${versionId}:${funnel.environment}`;
    if (publicationRequest.current.context !== context) {
      publicationRequest.current = {
        context,
        idempotencyKey: `campaign-review:${workflowJobId}:${versionId}:${crypto.randomUUID()}`,
      };
    }
    setFunnelActionState({ status: 'working', message: 'Publishing the approved funnel version...' });
    try {
      await api.post(
        `/api/saas/funnels/${funnel.id}/publications`,
        {
          versionId,
          environment: funnel.environment,
          idempotencyKey: publicationRequest.current.idempotencyKey,
          reason: 'campaign_review_approved_version_publish',
        },
        tenantHeaders(companyId, { 'Idempotency-Key': publicationRequest.current.idempotencyKey }),
      );
      if (activeReviewContext.current !== actionContext) return;
      const refreshed = await loadReview('refreshing');
      if (!refreshed) {
        setFunnelActionState({
          status: 'error',
          message: 'Publication was accepted, but the live state could not be confirmed. Refresh before retrying.',
        });
        return;
      }
      setFunnelActionState({ status: 'done', message: 'Funnel published. The live link is ready.' });
    } catch (err) {
      if (activeReviewContext.current !== actionContext) return;
      setFunnelActionState({ status: 'error', message: safeFailureMessage(err, 'funnel') });
    }
  }

  async function completeReviewFunnel(submission) {
    const funnelId = funnelWorkbench?.funnel?.id;
    if (!canManageFunnel || !funnelId || funnelCompletionState.status === 'working') return;
    const actionContext = activeReviewContextKey;
    const idempotencyKey = `campaign-review-funnel-completion:${crypto.randomUUID()}`;
    setFunnelCompletionState({
      status: 'working',
      message: 'Confirming review values and creating a new immutable funnel version...',
    });
    try {
      const response = await api.post(
        `/api/saas/funnels/${encodeURIComponent(funnelId)}/completion`,
        { ...submission, idempotencyKey },
        tenantHeaders(companyId, { 'Idempotency-Key': idempotencyKey }),
      );
      if (activeReviewContext.current !== actionContext) return;
      setFunnelWorkbench(response);
      const refreshed = await loadReview('refreshing');
      if (!refreshed) {
        setFunnelCompletionState({
          status: 'error',
          message: 'The funnel version was saved, but campaign review could not reload it. Refresh to confirm.',
        });
        return;
      }
      setFunnelCompletionState({
        status: 'done',
        message: response.completion?.readiness?.status === 'ready'
          ? 'Confirmed values saved. This exact funnel version is ready for approval.'
          : 'Confirmed values saved. Resolve the remaining required items before approval.',
      });
    } catch (requestError) {
      if (activeReviewContext.current !== actionContext) return;
      setFunnelCompletionState({
        status: 'error',
        message: safeFailureMessage(requestError, 'funnel'),
      });
    }
  }

  async function refreshReviewResearch() {
    const brand = funnelWorkbench?.funnel?.brand;
    const funnelId = funnelWorkbench?.funnel?.id;
    const selectedVersionId = funnelWorkbench?.selectedVersion?.id;
    if (!canManageFunnel
      || !brand?.id
      || !brand.websiteUrl
      || !funnelId
      || !selectedVersionId
      || funnelCompletionState.status === 'working') return;
    const actionContext = activeReviewContextKey;
    setFunnelCompletionState({ status: 'working', message: 'Refreshing website research candidates...' });
    try {
      await refreshSaasBrandResearch({
        companyId,
        brandId: brand.id,
        url: brand.websiteUrl,
        onProgress: (event) => {
          if (activeReviewContext.current !== actionContext) return;
          setFunnelCompletionState({
            status: 'working',
            message: event.label || 'Refreshing website research candidates...',
          });
        },
      });
      const response = await api.get(
        `/api/saas/funnels/${encodeURIComponent(funnelId)}?versionId=${encodeURIComponent(selectedVersionId)}`,
        tenantHeaders(companyId),
      );
      if (activeReviewContext.current !== actionContext) return;
      setFunnelWorkbench(response);
      setFunnelCompletionState({
        status: 'done',
        message: 'Website candidates refreshed. Review and explicitly confirm any values you want to use.',
      });
    } catch (requestError) {
      if (activeReviewContext.current !== actionContext) return;
      setFunnelCompletionState({
        status: 'error',
        message: safeFailureMessage(requestError, 'funnel'),
      });
    }
  }

  function openRevisionForm() {
    revisionRequest.current = { context: '', idempotencyKey: '' };
    setRevisionForm({ open: true, feedback: '', affectedAreas: [], status: 'idle', error: '' });
  }

  async function submitRevisionRequest() {
    const feedback = revisionForm.feedback.trim();
    if (feedback.length < 20) {
      setRevisionForm((current) => ({ ...current, error: 'Describe the requested changes in at least 20 characters.' }));
      return;
    }
    if (!revisionForm.affectedAreas.length) {
      setRevisionForm((current) => ({ ...current, error: 'Select at least one affected area.' }));
      return;
    }
    if (revisionForm.status === 'working') return;
    const context = `${companyId}:${workflowJobId}:${review.approvalReview?.hash}`;
    if (revisionRequest.current.context !== context) {
      revisionRequest.current = {
        context,
        idempotencyKey: `campaign-revision:${workflowJobId}:${crypto.randomUUID()}`,
      };
    }
    setRevisionForm((current) => ({ ...current, status: 'working', error: '' }));
    try {
      const response = await api.post(
        `/api/saas/workflows/${workflowJobId}/revisions`,
        {
          feedback,
          affectedAreas: revisionForm.affectedAreas,
          sourceSnapshotHash: review.approvalReview?.hash,
          idempotencyKey: revisionRequest.current.idempotencyKey,
        },
        tenantHeaders(companyId, { 'Idempotency-Key': revisionRequest.current.idempotencyKey }),
      );
      setRevisionForm((current) => ({ ...current, status: 'done', error: '' }));
      navigate(response.route || `workflow/${response.revision?.revisionWorkflowJobId}`);
    } catch (err) {
      setRevisionForm((current) => ({
        ...current,
        status: 'error',
        error: safeFailureMessage(err, 'workflow'),
      }));
    }
  }

  const summary = useMemo(() => campaignReviewSummary(review), [review]);
  const showInitialReviewLoading =
    Boolean(companyId) &&
    Boolean(workflowJobId) &&
    !review &&
    (!reviewContextCurrent || status === 'idle' || status === 'loading');
  const videoPresentation = useMemo(() => buildCampaignReviewVideos(review), [review]);
  const briefRoute = artifactRouteForReview(review, ['campaign_brief']);
  const scriptsRoute = artifactRouteForReview(review, ['selected_scripts']);
  const videosRoute = artifactRouteForReview(review, ['video', 'video_metadata']);
  const reviewTabs = [
    { id: 'brief', label: 'Brief' },
    { id: 'funnel', label: 'Funnel' },
    { id: 'scripts', label: 'Ad scripts', count: review?.selectedScripts?.count || 0 },
    { id: 'videos', label: 'Videos', count: videoPresentation.generatedCount },
    { id: 'publish', label: 'Publish' },
  ];

  function handleReviewTabKeyDown(event, index) {
    const keyOffsets = { ArrowLeft: -1, ArrowRight: 1 };
    let nextIndex = null;
    if (event.key in keyOffsets) {
      nextIndex = (index + keyOffsets[event.key] + reviewTabs.length) % reviewTabs.length;
    } else if (event.key === 'Home') {
      nextIndex = 0;
    } else if (event.key === 'End') {
      nextIndex = reviewTabs.length - 1;
    }
    if (nextIndex === null) return;
    event.preventDefault();
    const nextTab = reviewTabs[nextIndex];
    setActiveSection(nextTab.id);
    event.currentTarget.ownerDocument.getElementById(`campaign-review-tab-${nextTab.id}`)?.focus();
  }

  function renderReviewSection(sectionId) {
    if (sectionId === 'brief') {
      return (
        <ReviewSection
          title="Generated Brief"
          icon={<IconInfo size={16} />}
          action={<TextAction disabled={!briefRoute} onClick={() => navigate(briefRoute)}>Open brief</TextAction>}
        >
          <InspectableDetails value={review.sections?.brief} empty="No generated brief is available yet." />
        </ReviewSection>
      );
    }
    if (sectionId === 'funnel') {
      return (
        <FunnelReview
          review={review}
          funnelActionState={funnelActionState}
          openFunnelPreview={openFunnelPreview}
          publishFunnel={publishFunnel}
          completionPanel={funnelWorkbench ? (
            <FunnelReadinessPanel
              completion={funnelWorkbench.completion}
              selectedVersionId={funnelWorkbench.selectedVersion?.id}
              canManage={canManageFunnel}
              busy={funnelCompletionState.status === 'working'}
              message={funnelCompletionState.message}
              onComplete={completeReviewFunnel}
              onRefreshResearch={refreshReviewResearch}
            />
          ) : funnelCompletionState.status === 'loading' ? (
            <ProtectedPageLoader
              title="Loading funnel completion"
              body="Checking confirmed brand values and exact-version readiness."
              minHeight={220}
            />
          ) : funnelCompletionState.message ? (
            <EmptyInline>{funnelCompletionState.message}</EmptyInline>
          ) : null}
        />
      );
    }
    if (sectionId === 'scripts') {
      return <ScriptsReview review={review} scriptsRoute={scriptsRoute} navigate={navigate} />;
    }
    if (sectionId === 'videos') {
      return <VideosReview videoPresentation={videoPresentation} videosRoute={videosRoute} navigate={navigate} />;
    }
    if (sectionId === 'publish') {
      return <PublishReadiness review={review} navigate={navigate} />;
    }
    return null;
  }

  if (!isSaasSession) {
    return (
      <Page>
        <EmptyState title="SaaS session unavailable" body="Sign in with a SaaS account to review generated campaign assets." />
      </Page>
    );
  }

  return (
    <Page>
      <header style={topBarStyle}>
        <button onClick={() => navigate('campaigns')} style={backButtonStyle}>
          <span style={{ display: 'inline-flex', transform: 'rotate(180deg)' }}><IconChevron size={13} /></span>
          Campaigns
        </button>
        <div style={{ fontSize: 12, color: PALETTE.ink3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {session.user?.activeCompany?.name || 'Active company'}
        </div>
      </header>

      {showInitialReviewLoading ? (
        <ProtectedPageLoader />
      ) : status === 'error' ? (
        <EmptyState title="Campaign review unavailable" body={safeFailureMessage(error, 'approval')} tone="warn" />
      ) : review ? (
        <main style={mainStyle}>
          <section style={heroStyle}>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 14 }}>
              <Pill tone={statusTone(campaignReviewHeroStatus(review).status)}>
                {campaignReviewHeroStatus(review).label}
              </Pill>
              <span style={{ fontSize: 12, color: PALETTE.ink3 }}>
                {review.brand?.name || 'Campaign assets'}
              </span>
            </div>
            <h1 style={titleStyle}>{review.campaign?.name || review.sections?.overview?.name || 'Campaign review'}</h1>
            <p style={bodyStyle}>
              Review the generated brief, funnel direction, scripts, videos, and publish readiness before anything is approved for Meta.
            </p>
          </section>

          <section style={summaryGridStyle}>
            {summary.map((item) => (
              <Metric key={item.label} label={item.label} value={item.value} />
            ))}
          </section>

          {review.revision && (
            <section className="campaign-review-revision-context" aria-label="Revision context">
              <div>
                <div className="campaign-review-revision-title">Revision {labelize(review.revision.status)}</div>
                <p>{review.revision.feedback}</p>
              </div>
              <div className="campaign-review-revision-areas">
                {review.revision.affectedAreas.map((area) => <Pill key={area} tone="accent">{labelize(area)}</Pill>)}
              </div>
            </section>
          )}

          <section className="campaign-review-workspace">
            <div className="campaign-review-reviewer">
              <div className="campaign-review-tabs" role="tablist" aria-label="Campaign review sections">
                {reviewTabs.map((tab, index) => (
                  <button
                    key={tab.id}
                    type="button"
                    role="tab"
                    id={`campaign-review-tab-${tab.id}`}
                    aria-controls={`campaign-review-panel-${tab.id}`}
                    aria-selected={activeSection === tab.id}
                    tabIndex={activeSection === tab.id ? 0 : -1}
                    data-active={activeSection === tab.id}
                    className="campaign-review-tab"
                    onClick={() => setActiveSection(tab.id)}
                    onKeyDown={(event) => handleReviewTabKeyDown(event, index)}
                  >
                    {tab.label}
                    {Number.isInteger(tab.count) && <span className="campaign-review-tab-count">{tab.count}</span>}
                  </button>
                ))}
              </div>

              {reviewTabs.map((tab) => (
                <div
                  key={tab.id}
                  className="campaign-review-tab-panel"
                  role="tabpanel"
                  id={`campaign-review-panel-${tab.id}`}
                  aria-labelledby={`campaign-review-tab-${tab.id}`}
                  hidden={activeSection !== tab.id}
                >
                  {activeSection === tab.id ? renderReviewSection(tab.id) : null}
                </div>
              ))}
            </div>

            <ApprovalRail
              review={review}
              workflowJobId={workflowJobId}
              companyId={companyId}
              actionState={actionState}
              runAction={runAction}
              onRequestChanges={openRevisionForm}
            />
          </section>

          <section style={{ ...panelStyle, marginTop: 18 }}>
            <SectionTitle
              icon={<IconExternal size={16} />}
              title="Campaign Assets"
              action={<TextAction onClick={() => navigate('library')}>Open asset library</TextAction>}
            />
            <ListPreview
              items={review.artifacts || []}
              empty="No signed artifact links are available yet. Generated files will appear here when the workflow stores them."
              render={(artifact) => (
                <div style={artifactRowStyle}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: PALETTE.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {artifact.label || labelize(artifact.type, 'Artifact')}
                    </div>
                    <div style={{ fontSize: 12, color: PALETTE.ink3, marginTop: 4 }}>
                      {labelize(artifact.type)} · {artifact.contentType || 'file'} · {fmtDateTime(artifact.createdAt)}
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Pill tone={statusTone(artifact.availability?.status)}>{labelize(artifact.availability?.status)}</Pill>
                    {artifact.action?.type === 'internal_route' && artifact.action.route ? (
                      <TextAction onClick={() => navigate(artifact.action.route)}>Open</TextAction>
                    ) : isVideoArtifact(artifact) ? (
                      // Never hand out a raw video URL: a top-level tab has the
                      // browser's own Download button. Videos play in the tab.
                      <span style={{ fontSize: 12, color: PALETTE.ink3 }}>Plays in the Videos tab</span>
                    ) : artifact.signedUrl?.url ? (
                      <a href={artifact.signedUrl.url} target="_blank" rel="noreferrer" style={linkButtonStyle}>
                        Open <IconExternal size={12} />
                      </a>
                    ) : (
                      <span style={{ fontSize: 12, color: PALETTE.ink3 }}>{artifact.availability?.message}</span>
                    )}
                  </div>
                </div>
              )}
            />
          </section>

        </main>
      ) : (
        <EmptyState title="Campaign review unavailable" body="Campaign review data is unavailable." tone="warn" />
      )}
      {revisionForm.open && (
        <RevisionRequestDialog
          form={revisionForm}
          setForm={setRevisionForm}
          onClose={() => revisionForm.status !== 'working' && setRevisionForm((current) => ({ ...current, open: false }))}
          onSubmit={submitRevisionRequest}
        />
      )}
    </Page>
  );
}

function ReviewSection({ title, icon, action = null, children }) {
  return (
    <section className="campaign-review-section">
      <SectionTitle icon={icon} title={title} action={action} />
      {children}
    </section>
  );
}

function ApprovalRail({ review, workflowJobId, companyId, actionState, runAction, onRequestChanges }) {
  const funnelApprovalReadiness = review.funnel?.generatedVersion?.approvalReadiness;
  const funnelReadinessBlockers = [
    ...(review.funnel?.generatedVersion?.readinessManifest?.required || []),
    ...(review.funnel?.generatedVersion?.readinessManifest?.conditional || []),
  ].filter((item) => item.status !== 'ready');

  return (
    <aside style={actionPanelStyle} className="campaign-review-approval-rail" aria-label="Campaign approval controls">
      <SectionTitle icon={<IconTarget size={16} />} title="Campaign Approval" />
      <div style={{ display: 'grid', gap: 10, marginBottom: 16 }}>
        <DetailRow label="Approval" value={labelize(review.approval?.status, 'Pending')} tone={statusTone(review.approval?.status)} />
        <DetailRow label="Revision" value={`Revision ${review.approval?.revisionNumber || 1}`} />
        <DetailRow label="Meta intent" value={labelize(review.publish?.status, 'Not requested')} tone={statusTone(review.publish?.status)} />
      </div>
      {funnelApprovalReadiness?.status === 'blocked' && (
        <div className="campaign-review-approval-notice" role="status">
          <strong>Complete the funnel before approval.</strong>
          <span>
            {funnelReadinessBlockers.length
              ? `${funnelReadinessBlockers.map((item) => item.label).join(', ')} must be confirmed in the funnel editor.`
              : funnelApprovalReadiness.message}
          </span>
        </div>
      )}
      {review.approvalReview?.changed === true && (
        <div className="campaign-review-approval-notice" role="status">
          <strong>Current assets need re-approval.</strong>
          <span>
            Revision {review.activeApproval?.revisionNumber || 1} remains the active immutable approval.
            {' '}{review.approvalReview.changedAdCount || 0} ad item changes are outside that snapshot.
          </span>
        </div>
      )}
      {review.approval?.status === 'pending' && review.approval?.supersedesApprovalRequestId && (
        <div className="campaign-review-approval-notice" role="status">
          <strong>Changed assets are ready to review.</strong>
          <span>Approve revision {review.approval.revisionNumber} to govern the next Meta publish intent.</span>
        </div>
      )}
      <div className="campaign-review-approval-actions">
        <Button
          variant="accent"
          size="sm"
          disabled={!review.actions?.canApprove || actionState.status === 'working'}
          onClick={() => runAction('Approval', () => api.post(
            `/api/saas/workflows/${workflowJobId}/approval`,
            {
              decision: 'approved',
              reason: 'approved_from_campaign_review',
              reviewSnapshotHash: review.approvalReview?.hash,
            },
            tenantHeaders(companyId),
          ))}
        >
          <IconCheck size={13} /> Approve
        </Button>
        <Button
          variant="ghost"
          size="sm"
          disabled={!review.actions?.canRequestChanges || actionState.status === 'working'}
          onClick={onRequestChanges}
        >
          Request changes
        </Button>
        {review.actions?.canStartReapproval && (
          <Button
            variant="accent"
            size="sm"
            disabled={actionState.status === 'working'}
            onClick={() => runAction('Re-approval revision', () => api.post(
              `/api/saas/workflows/${workflowJobId}/reapprovals`,
              {
                sourceApprovalRequestId: review.activeApproval?.id,
                reviewSnapshotHash: review.approvalReview?.hash,
              },
              tenantHeaders(companyId),
            ))}
          >
            <IconSpark size={13} /> Review changed assets
          </Button>
        )}
        <Button
          variant="soft"
          size="sm"
          disabled={!review.actions?.canPreparePublish || actionState.status === 'working'}
          onClick={() => runAction('Meta publish preparation', () => api.post(
            `/api/saas/workflows/${workflowJobId}/publish-intents/meta`,
            {},
            tenantHeaders(companyId),
          ))}
        >
          <IconSpark size={13} /> Prepare Meta publish
        </Button>
      </div>
      {actionState.message && (
        <div style={{
          marginTop: 14,
          fontSize: 12,
          color: actionState.status === 'error' ? PALETTE.danger : PALETTE.ink3,
        }} role="status" aria-live="polite">
          {actionState.message}
        </div>
      )}
    </aside>
  );
}

function RevisionRequestDialog({ form, setForm, onClose, onSubmit }) {
  const dialogRef = useRef(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  useEffect(() => {
    dialogRef.current?.focus();
    function onKeyDown(event) {
      if (event.key === 'Escape') onCloseRef.current();
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  function toggleArea(area) {
    setForm((current) => ({
      ...current,
      error: '',
      affectedAreas: current.affectedAreas.includes(area)
        ? current.affectedAreas.filter((item) => item !== area)
        : [...current.affectedAreas, area],
    }));
  }

  return (
    <div className="campaign-revision-dialog-backdrop" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section
        ref={dialogRef}
        className="campaign-revision-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="campaign-revision-title"
        tabIndex={-1}
      >
        <div className="campaign-revision-dialog-heading">
          <div>
            <div className="campaign-revision-kicker">Campaign revision</div>
            <h2 id="campaign-revision-title">What should change?</h2>
          </div>
          <button type="button" className="campaign-revision-close" onClick={onClose} disabled={form.status === 'working'} aria-label="Close">×</button>
        </div>
        <label className="campaign-revision-field">
          <span>Feedback</span>
          <textarea
            rows={5}
            maxLength={2000}
            value={form.feedback}
            onChange={(event) => setForm((current) => ({ ...current, feedback: event.target.value, error: '' }))}
            placeholder="Explain what needs to change and what a successful revision should accomplish."
            disabled={form.status === 'working'}
          />
          <small>{form.feedback.trim().length}/2000 · minimum 20 characters</small>
        </label>
        <fieldset className="campaign-revision-areas">
          <legend>Affected areas</legend>
          <div>
            {REVISION_AREAS.map((area) => (
              <label key={area.id}>
                <input
                  type="checkbox"
                  checked={form.affectedAreas.includes(area.id)}
                  onChange={() => toggleArea(area.id)}
                  disabled={form.status === 'working'}
                />
                <span>{area.label}</span>
              </label>
            ))}
          </div>
        </fieldset>
        <div className="campaign-revision-impact">
          <IconInfo size={16} />
          <span>To keep the campaign coherent, the brief, funnel, scripts, videos, and publish snapshot will be regenerated as one new version. Existing review history stays unchanged.</span>
        </div>
        {form.error && <div className="campaign-revision-error" role="alert">{form.error}</div>}
        <div className="campaign-revision-dialog-actions">
          <Button variant="ghost" size="sm" onClick={onClose} disabled={form.status === 'working'}>Cancel</Button>
          <Button variant="accent" size="sm" onClick={onSubmit} disabled={form.status === 'working'}>
            {form.status === 'working' ? 'Starting revision…' : 'Start revision'}
          </Button>
        </div>
      </section>
    </div>
  );
}

function FunnelReview({
  review,
  funnelActionState,
  openFunnelPreview,
  publishFunnel,
  completionPanel,
}) {
  return (
    <ReviewSection
      title="Lead Funnel"
      icon={<IconTarget size={16} />}
      action={review.funnel?.generatedVersion?.number ? <Pill tone="neutral">Version {review.funnel.generatedVersion.number}</Pill> : null}
    >
      {review.funnel?.status === 'available' ? (
        <div>
          <div style={funnelStatusStyle}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: PALETTE.ink, overflowWrap: 'anywhere' }}>
                {review.funnel.name || 'Campaign funnel'}
              </div>
              <div style={{ marginTop: 5, fontSize: 12, color: PALETTE.ink3 }}>
                Version {review.funnel.generatedVersion?.number || '-'} · {labelize(review.funnel.generatedVersion?.status)}
              </div>
            </div>
            <Pill tone={statusTone(review.funnel.publication?.status)}>
              {labelize(review.funnel.publication?.status)}
            </Pill>
          </div>
          <FunnelJourney funnel={review.funnel} />
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 14 }}>
            <Button
              variant="soft"
              size="sm"
              disabled={review.funnel.preview?.status !== 'available' || funnelActionState.status === 'working'}
              onClick={openFunnelPreview}
            >
              <IconExternal size={12} /> Preview draft
            </Button>
            <Button
              variant="accent"
              size="sm"
              disabled={!review.actions?.canPublishFunnel || funnelActionState.status === 'working'}
              onClick={publishFunnel}
            >
              <IconSpark size={12} /> Publish funnel
            </Button>
            {review.funnel.publication?.url && (
              <a href={review.funnel.publication.url} target="_blank" rel="noreferrer" style={linkButtonStyle}>
                {review.funnel.publication.isGeneratedVersionActive ? 'Open live funnel' : 'Open current live funnel'} <IconExternal size={12} />
              </a>
            )}
          </div>
          <div style={{ marginTop: 10, fontSize: 12, lineHeight: 1.5, color: PALETTE.ink3 }}>
            {review.funnel.publishReadiness?.message || 'Publication readiness is unavailable.'}
          </div>
          <div style={funnelDetailsStyle}>
            <Detail label="Approval" value={labelize(review.funnel.generatedVersion?.approvalStatus)} />
            <Detail label="Publication revision" value={review.funnel.publication?.revision ? `Revision ${review.funnel.publication.revision}` : 'Not published'} />
            <Detail label="Active version" value={review.funnel.publication?.versionNumber ? `Version ${review.funnel.publication.versionNumber}` : 'None'} />
          </div>
          {review.funnel.publication?.status === 'published' && !review.funnel.publication?.isGeneratedVersionActive && (
            <div style={{ marginTop: 12, fontSize: 12, lineHeight: 1.5, color: PALETTE.ink2 }}>
              This generated version is a draft. Version {review.funnel.publication.versionNumber} remains live until a new approved version is published.
            </div>
          )}
          {review.funnel.publication?.action === 'rollback' && (
            <div style={{ marginTop: 12, fontSize: 12, lineHeight: 1.5, color: PALETTE.ink2 }}>
              The live pointer was rolled back to version {review.funnel.publication.versionNumber} without redeploying the renderer.
            </div>
          )}
          {review.funnel.publication?.status === 'needs_attention' && (
            <div style={{ marginTop: 12, fontSize: 12, lineHeight: 1.5, color: PALETTE.warn }}>
              The publication record needs operator attention before this funnel can be opened safely.
            </div>
          )}
          {funnelActionState.message && (
            <div
              style={{ marginTop: 12, fontSize: 12, color: funnelActionState.status === 'error' ? PALETTE.danger : PALETTE.ink3 }}
              role="status"
              aria-live="polite"
            >
              {funnelActionState.message}
            </div>
          )}
          {completionPanel && <div style={{ marginTop: 18 }}>{completionPanel}</div>}
        </div>
      ) : (
        <EmptyInline>{review.funnel?.message || 'A versioned funnel has not been generated yet.'}</EmptyInline>
      )}
    </ReviewSection>
  );
}

function ScriptHook({ value }) {
  const hook = String(value || '').trim();
  if (!hook) return null;
  if (hook.length <= 220) return <div className="campaign-review-copy">{hook}</div>;
  return (
    <details className="campaign-review-copy-details">
      <summary>{hook.slice(0, 180)}… <span>Read full hook</span></summary>
      <div className="campaign-review-copy">{hook}</div>
    </details>
  );
}

function ScriptsReview({ review, scriptsRoute, navigate }) {
  const scripts = review.selectedScripts?.items || [];
  return (
    <ReviewSection
      title="Selected Ad Scripts"
      icon={<IconPlay size={16} />}
      action={(
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
          <Pill tone={review.selectedScripts?.status === 'available' ? 'neutral' : 'warn'}>
            {review.selectedScripts?.count || 0} selected
          </Pill>
          <TextAction disabled={!scriptsRoute} onClick={() => navigate(scriptsRoute)}>Open scripts</TextAction>
        </span>
      )}
    >
      {review.selectedScripts?.status === 'partial' && (
        <div style={{ marginBottom: 10, fontSize: 12, lineHeight: 1.5, color: PALETTE.warn }}>
          {review.selectedScripts.message}
        </div>
      )}
      {scripts.length ? (
        <div className="campaign-review-script-grid">
          {scripts.map((item, index) => (
            <article className="campaign-review-script-card" key={item?.id || `script-${index + 1}`}>
              <div className="campaign-review-card-title">{item.title || item.id || `Script ${index + 1}`}</div>
              <div className="campaign-review-card-meta">
                {labelize(item.angle, 'Details unavailable')}{item.estimatedSeconds ? ` · ${item.estimatedSeconds}s` : ''}
              </div>
              <ScriptHook value={item.hook} />
            </article>
          ))}
        </div>
      ) : <EmptyInline>{review.selectedScripts?.message || 'No script selections have been generated yet.'}</EmptyInline>}
    </ReviewSection>
  );
}

function VideosReview({ videoPresentation, videosRoute, navigate }) {
  const { items, generatedCount, playableCount, unavailableCount } = videoPresentation;
  return (
    <ReviewSection
      title="Generated Videos"
      icon={<IconVideo size={16} />}
      action={<TextAction disabled={!videosRoute} onClick={() => navigate(videosRoute)}>Open videos</TextAction>}
    >
      <div className="campaign-review-video-summary" role="status">
        <strong>{generatedCount} generated</strong>
        <span>{playableCount} playable</span>
        <span>{unavailableCount} unavailable</span>
      </div>
      {items.length ? (
        <div className="campaign-review-video-grid">
          {items.map((item) => (
            <article className="campaign-review-video-card" key={item.key}>
              {item.signedUrl ? (
                <ProtectedVideo controls preload="metadata" className="campaign-review-video" aria-label={item.title}>
                  <source src={item.signedUrl} type={item.contentType} />
                </ProtectedVideo>
              ) : (
                <div className="campaign-review-video-unavailable" role="status">
                  <IconVideo size={20} />
                  <Pill tone="warn">{labelize(item.availability.status)}</Pill>
                  <span>{item.availability.message}</span>
                </div>
              )}
              <div className="campaign-review-video-details">
                <div className="campaign-review-card-title">{item.title}</div>
                <div className="campaign-review-card-meta">
                  {item.durationSeconds ? `${item.durationSeconds}s` : item.concept || 'Video asset'}
                </div>
                {item.filename && <div className="campaign-review-filename">{item.filename}</div>}
              </div>
            </article>
          ))}
        </div>
      ) : <EmptyInline>No video assets have been generated yet.</EmptyInline>}
    </ReviewSection>
  );
}

function PublishReadiness({ review, navigate }) {
  return (
    <ReviewSection
      title="Meta Publish Readiness"
      icon={<IconInfo size={16} />}
      action={<TextAction onClick={() => navigate('reports')}>Open reports</TextAction>}
    >
      <p style={{ margin: '0 0 16px', color: PALETTE.ink2, fontSize: 13, lineHeight: 1.5 }}>
        Publishing stays paused until an owner or admin approves these campaign assets and prepares the tenant Meta connection payload.
      </p>
      <div style={publishGridStyle}>
        <Detail label="Provider" value={labelize(review.publish?.provider || 'meta')} />
        <Detail label="Intent status" value={labelize(review.publish?.status || 'not_requested')} />
        <Detail label="Requested" value={fmtDateTime(review.publish?.createdAt)} />
        <Detail label="Finished" value={fmtDateTime(review.publish?.finishedAt)} />
      </div>
      <KeyValueList
        entries={compactJsonEntries(review.publish?.providerObjectIds)}
        empty="No provider object ids have been recorded yet."
      />
    </ReviewSection>
  );
}

function FunnelJourney({ funnel }) {
  const approved = funnel.generatedVersion?.approvalStatus === 'approved';
  const published = funnel.publication?.status === 'published' && funnel.publication?.isGeneratedVersionActive;
  const steps = [
    { label: 'Preview draft', state: funnel.preview?.status === 'available' ? 'ready' : 'blocked' },
    { label: 'Approve version', state: approved ? 'complete' : 'pending' },
    { label: 'Publish funnel', state: published ? 'complete' : funnel.publishReadiness?.status === 'ready' ? 'ready' : 'pending' },
    { label: 'Open live funnel', state: published && funnel.publication?.url ? 'complete' : 'pending' },
  ];
  return (
    <div style={funnelJourneyStyle} aria-label="Funnel website publication steps">
      {steps.map((step, index) => (
        <div key={step.label} style={funnelJourneyStepStyle}>
          <span style={{ ...funnelJourneyIndexStyle, color: step.state === 'complete' ? PALETTE.success : PALETTE.ink3 }}>
            {index + 1}
          </span>
          <span style={{ fontSize: 11, fontWeight: 700, color: step.state === 'blocked' ? PALETTE.warn : PALETTE.ink2 }}>
            {step.label}
          </span>
        </div>
      ))}
    </div>
  );
}

function Page({ children }) {
  return (
    <div style={{ minHeight: '100vh', background: PALETTE.bg, color: PALETTE.ink, fontFamily: 'Inter, sans-serif' }}>
      {children}
    </div>
  );
}

function SectionTitle({ icon, title, action = null }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, color: PALETTE.ink, fontSize: 14, fontWeight: 700, marginBottom: 14 }}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}><span style={{ color: PALETTE.accent }}>{icon}</span>{title}</span>
      {action}
    </div>
  );
}

function Metric({ label, value }) {
  return (
    <div style={metricStyle}>
      <div style={{ fontSize: 11, color: PALETTE.ink3, textTransform: 'uppercase', letterSpacing: 0 }}>{label}</div>
      <div style={{ marginTop: 8, fontSize: 18, color: PALETTE.ink, fontWeight: 700 }}>{value}</div>
    </div>
  );
}

function Detail({ label, value }) {
  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 11, color: PALETTE.ink3, marginBottom: 5 }}>{label}</div>
      <div style={{ fontSize: 13, color: PALETTE.ink, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {value}
      </div>
    </div>
  );
}

function DetailRow({ label, value, tone }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
      <span style={{ fontSize: 12, color: PALETTE.ink3 }}>{label}</span>
      <Pill tone={tone}>{value}</Pill>
    </div>
  );
}

function KeyValueList({ entries, empty }) {
  if (!entries.length) return <EmptyInline>{empty}</EmptyInline>;
  return (
    <div style={{ display: 'grid', gap: 10 }}>
      {entries.map(([key, value]) => (
        <div key={key} style={kvRowStyle}>
          <div style={{ color: PALETTE.ink3, fontSize: 12 }}>{labelize(key)}</div>
          <div style={{ color: PALETTE.ink, fontSize: 13, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {valuePreview(value)}
          </div>
        </div>
      ))}
    </div>
  );
}

function ListPreview({ items, empty, render }) {
  if (!Array.isArray(items) || !items.length) return <EmptyInline>{empty}</EmptyInline>;
  return <div style={{ display: 'grid', gap: 10 }}>{items.slice(0, 8).map((item, index) => <div key={item?.id || item?.filename || index}>{render(item, index)}</div>)}</div>;
}

function TextAction({ children, onClick, disabled = false }) {
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      style={{
        background: 'none', border: 0, padding: 0,
        color: disabled ? PALETTE.ink3 : PALETTE.accentDeep,
        cursor: disabled ? 'not-allowed' : 'pointer',
        fontSize: 12, fontWeight: 700,
      }}
    >
      {disabled ? 'Unavailable' : children}
    </button>
  );
}

function InspectableDetails({ value, empty }) {
  const entries = compactJsonEntries(value);
  if (!entries.length) return <EmptyInline>{empty}</EmptyInline>;
  return (
    <div style={{ display: 'grid', gap: 10 }}>
      {entries.map(([key, item]) => (
        <div key={key} style={kvRowStyle}>
          <div style={{ color: PALETTE.ink3, fontSize: 11, textTransform: 'uppercase' }}>{labelize(key)}</div>
          {Array.isArray(item) ? (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {item.slice(0, 8).map((part, index) => <Pill key={`${key}-${index}`} tone="neutral">{typeof part === 'object' ? valuePreview(part) : String(part)}</Pill>)}
            </div>
          ) : item && typeof item === 'object' ? (
            <div style={{ display: 'grid', gap: 6 }}>
              {compactJsonEntries(item).slice(0, 6).map(([nestedKey, nestedValue]) => (
                <div key={nestedKey} style={{ fontSize: 12, color: PALETTE.ink2 }}><strong>{labelize(nestedKey)}:</strong> {valuePreview(nestedValue)}</div>
              ))}
            </div>
          ) : <div style={{ color: PALETTE.ink, fontSize: 13, fontWeight: 700, lineHeight: 1.45 }}>{String(item)}</div>}
        </div>
      ))}
    </div>
  );
}

function EmptyInline({ children }) {
  return (
    <div style={{
      padding: 14,
      borderRadius: 8,
      background: PALETTE.surfaceAlt,
      color: PALETTE.ink3,
      fontSize: 13,
      lineHeight: 1.45,
    }}>
      {children}
    </div>
  );
}

function EmptyState({ title, body, tone = 'neutral' }) {
  return (
    <div style={{ maxWidth: 680, margin: '80px auto', padding: 28, border: `1px solid ${PALETTE.line}`, borderRadius: 10, background: tone === 'warn' ? PALETTE.warnSoft : PALETTE.surface }}>
      <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 8 }}>{title}</div>
      <div style={{ fontSize: 14, color: tone === 'warn' ? PALETTE.warn : PALETTE.ink2, lineHeight: 1.55 }}>{body}</div>
    </div>
  );
}

const topBarStyle = {
  height: 56,
  padding: '0 clamp(16px, 4vw, 32px)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 16,
  borderBottom: `1px solid ${PALETTE.lineSoft}`,
  background: PALETTE.bg,
  position: 'sticky',
  top: 0,
  zIndex: 3,
};

const backButtonStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 7,
  background: 'none',
  border: 'none',
  color: PALETTE.ink3,
  cursor: 'pointer',
  fontSize: 13,
  fontFamily: 'Inter, sans-serif',
  padding: 0,
};

const mainStyle = {
  maxWidth: 1220,
  margin: '0 auto',
  padding: 'clamp(24px, 4vw, 44px) clamp(16px, 4vw, 40px) 64px',
};

const heroStyle = {
  minWidth: 0,
  marginBottom: 18,
};

const titleStyle = {
  fontFamily: 'Instrument Serif, serif',
  fontSize: 48,
  lineHeight: 1.04,
  fontWeight: 400,
  letterSpacing: 0,
  margin: 0,
  overflowWrap: 'anywhere',
};

const bodyStyle = {
  maxWidth: 680,
  margin: '16px 0 0',
  fontSize: 15,
  lineHeight: 1.55,
  color: PALETTE.ink2,
};

const panelStyle = {
  padding: 18,
  border: `1px solid ${PALETTE.line}`,
  borderRadius: 10,
  background: PALETTE.surface,
};

const actionPanelStyle = {
  ...panelStyle,
  alignSelf: 'start',
};

const summaryGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
  gap: 12,
  marginBottom: 18,
};

const funnelStatusStyle = {
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: 12,
};

const funnelDetailsStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
  gap: 14,
  marginTop: 16,
  paddingTop: 14,
  borderTop: `1px solid ${PALETTE.lineSoft}`,
};

const funnelJourneyStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
  gap: 8,
  marginTop: 14,
  padding: '12px 0',
  borderTop: `1px solid ${PALETTE.lineSoft}`,
  borderBottom: `1px solid ${PALETTE.lineSoft}`,
};

const funnelJourneyStepStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 7,
  minWidth: 0,
};

const funnelJourneyIndexStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 20,
  height: 20,
  flex: '0 0 20px',
  border: `1px solid ${PALETTE.line}`,
  borderRadius: '50%',
  fontSize: 10,
  fontWeight: 700,
};

const metricStyle = {
  padding: 16,
  border: `1px solid ${PALETTE.line}`,
  borderRadius: 10,
  background: PALETTE.surface,
};

const kvRowStyle = {
  display: 'grid',
  gridTemplateColumns: 'minmax(110px, 0.45fr) minmax(0, 1fr)',
  gap: 12,
  alignItems: 'center',
  padding: '10px 0',
  borderBottom: `1px solid ${PALETTE.lineSoft}`,
};

const artifactRowStyle = {
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr) auto',
  gap: 14,
  alignItems: 'center',
  padding: '12px 0',
  borderBottom: `1px solid ${PALETTE.lineSoft}`,
};

const publishGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
  gap: 14,
  marginBottom: 14,
};

const linkButtonStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '8px 12px',
  borderRadius: 8,
  border: `1px solid ${PALETTE.line}`,
  color: PALETTE.ink,
  textDecoration: 'none',
  fontSize: 13,
  fontWeight: 'var(--button-font-weight, 400)',
};
